import { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { useMediaQuery } from 'usehooks-ts'


import { useAuth } from '@features/auth'
import { CommentButton, DeleteProduct, useCartItemTotal } from '@features/cart'
import { useCartStore } from '@features/cart/model/useCartStore'
import ControlCart from '@features/cart/ui/control'
import { FavoriteButton } from '@features/favorite'
import { InStock } from '@features/in-stock'
import { useErrorInfoModal } from '@features/modal'
import { useAddressStore } from '@features/shipping-methods'

import { SimilarProductsTrigger } from '@widgets/products-similar'


import { Product, useDropCartItemMutation } from '@shared/generated'
import { useProductUnit } from '@shared/hooks'
import { cn } from '@shared/lib/cn'
import ButtonDelay from '@shared/ui/button-delay'
import ImageWithBg from '@shared/ui/image-with-bg'
import { formatPrice } from '@shared/uttils'

import styles from './styles.module.scss'

export interface ProductCartCardProps {
  data: Product;
  amount: number;
  className?: string;
  outStockProduct?: boolean;
}

function ProductCart (props: ProductCartCardProps) {
  const { openErrorInfoModal } = useErrorInfoModal()
  const { data, className, amount, outStockProduct } = props
  const isTablet = useMediaQuery('(max-width: 991px)')
  const isMobileSm = useMediaQuery('(max-width: 375px)')
  const [ isDeleted, setIsDeleted ] = useState(false)
  const [ fetchDropCartItem ] = useDropCartItemMutation()
  const { removeProduct, currentShopId } = useCartStore()
  const { isAuthorized } = useAuth()
  const { shopId } = useAddressStore()
  const { t: tc } = useTranslation('common')
  const { t: tca } = useTranslation('cart')
  const { t: te } = useTranslation('errors')
  const cartItemTotalPrice = useCartItemTotal(data, amount)
  const cartItemTotalOldPrice = useCartItemTotal(data, amount, true)

  const handleDelete = async () => {
    if (isAuthorized) {
      try {
        await fetchDropCartItem({
          variables: {
            shopId: shopId,
            productId: data.id,
          },
        })

        removeProduct(data.id)
      }
      catch (error) {
        console.error('Failed to delete item from cart:', error)
        setIsDeleted(false)
        openErrorInfoModal({
          description: te('drop_cart_item'),
        })
      }
    }
    else {
      removeProduct(data.id)
    }
  }

  const { unit } = useProductUnit({
    isWeightProduct: data.isWeightProduct as boolean,
    value: amount,
    price: cartItemTotalPrice as number,
    t: tc,
  })

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (outStockProduct) {
      e.preventDefault()
    }
  }

  return (
    <div
      className={cn(styles.root, className, {
        [styles.out]: outStockProduct,
      })}
    >
      <Link
        to={`/product/${data.slug}`}
        className={styles.root__image}
        onClick={handleLinkClick}
      >
        <ImageWithBg
          className={styles['root__image-inner']}
          src={data.preview?.url || '/icons/flower-grey.svg'}
        />
      </Link>

      <div className={styles.root__info}>
        <Link
          to={`/product/${data.slug}`}
          className={styles.root__name}
          onClick={handleLinkClick}
        >
          {data.name}
        </Link>

        <div className={styles.root__stock}>
          <InStock data={data} />
        </div>

        {!isDeleted && !outStockProduct && (
          <ControlCart
            classNameControl={styles.root__control}
            isTablet={isTablet}
            product={data}
            size="small"
          />
        )}
        {isDeleted && !outStockProduct && (
          <ButtonDelay
            className={styles.root__control}
            duration={3000}
            onFinish={async () => {
              await handleDelete()
            }}
            onClick={() => {
              setIsDeleted(false)
            }}
          >
            {tca('preview.cart_return')}
          </ButtonDelay>
        )}

        {!outStockProduct && (
          <div className={styles['root__info-wrapper']}>
            <div className={styles.root__price}>
              {cartItemTotalPrice && (
                <div className={styles['root__price-value']}>
                  {formatPrice(cartItemTotalPrice)}
                </div>
              )}
              {!!data.priceOffer?.oldPrice && cartItemTotalOldPrice && (
                <div className={styles['root__price-old']}>
                  {formatPrice(cartItemTotalOldPrice as number)}
                </div>
              )}
            </div>

            <span className={styles.root__count}>
              {amount} {unit} x {formatPrice(data.priceOffer?.price as number)}
            </span>
          </div>
        )}

        {!outStockProduct && (
          <div className={styles['root__btn-group']}>
            {isMobileSm ? (
              <>
                <FavoriteButton id={data.id} isLarge />
                <DeleteProduct onClick={() => setIsDeleted(true)} />
                <CommentButton product={data} />
              </>
            ) : (
              <>
                <CommentButton product={data} />
                <FavoriteButton id={data.id} isLarge />
                <DeleteProduct onClick={() => setIsDeleted(true)} />
              </>
            )}
          </div>
        )}

        {outStockProduct && (
          <div className={styles['root__btn-group']}>
            <SimilarProductsTrigger productId={data.id} />

            <DeleteProduct
              onClick={async () => {
                if (isAuthorized) {
                  try {
                    await fetchDropCartItem({
                      variables: {
                        shopId: currentShopId as string,
                        productId: data.id,
                      },
                    })

                    removeProduct(data.id)
                  }
                  catch (error) {
                    console.error('fetchDropCartItem error: ', error)
                    setIsDeleted(false)
                    openErrorInfoModal({
                      description: te('drop_cart_item'),
                    })
                  }
                }
                else {
                  removeProduct(data.id)
                }
              }}
            />
          </div>
        )}
      </div>
    </div>
  )
}

function areEqual (
  prev: Readonly<ProductCartCardProps>,
  next: Readonly<ProductCartCardProps>,
) {
  return (
    prev.amount === next.amount &&
    prev.className === next.className &&
    prev.data.id === next.data.id &&
    prev.data.price === next.data.price &&
    prev.data.oldPrice === next.data.oldPrice
  )
}

export default memo(ProductCart, areEqual)
